import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Progress, Button, Modal, Descriptions, Tag, Typography, Spin, Alert, Empty } from 'antd';
import { SyncOutlined, InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { getLatestScores, computeScores } from '../api/trustgridApi';
import { colors } from '../../../theme/designTokens';

const { Title, Text } = Typography;

const ScoreGauge = ({ title, score, band, color, onClick, breakdown }) => {
  return (
    <Card
      hoverable
      onClick={onClick}
      style={{ textAlign: 'center', height: '100%', cursor: 'pointer' }}
    >
      <Title level={4}>{title}</Title>
      <Progress
        type="circle"
        percent={score}
        format={(percent) => `${percent.toFixed(1)}%`}
        strokeColor={color}
        strokeWidth={8}
        width={120}
      />
      <div style={{ marginTop: 16 }}>
        <Tag color={color} style={{ fontSize: 14 }}>{band}</Tag>
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        Click for details
      </Text>
    </Card>
  );
};

const ThreeScoreGauges = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [scores, setScores] = useState(null);
  const [error, setError] = useState(null);
  const [drilldownVisible, setDrilldownVisible] = useState(false);
  const [selectedScore, setSelectedScore] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchScores();
    }
  }, [projectId]);

  const fetchScores = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getLatestScores(projectId);
      setScores(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No score snapshots found. Run a scan to generate scores.');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch scores');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComputeScores = async () => {
    setComputing(true);
    setError(null);
    try {
      await computeScores(projectId);
      await fetchScores();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to compute scores');
    } finally {
      setComputing(false);
    }
  };

  const openDrilldown = (scoreType, breakdown) => {
    setSelectedScore({ type: scoreType, breakdown });
    setDrilldownVisible(true);
  };

  // Helper to get tier-based color for any score
  const getScoreColor = (score) => {
    if (score >= 80) return colors.trustScore.excellent;
    if (score >= 60) return colors.trustScore.good;
    return colors.trustScore.poor;
  };

  const getScoreBand = (score, type) => {
    // Tier-based color mapping: 80-100 (excellent), 60-79 (good), <60 (poor)
    const color = getScoreColor(score);
    if (type === 'trust') {
      if (score >= 80) return { text: 'Excellent Trust', color };
      if (score >= 60) return { text: 'Moderate Risk', color };
      return { text: 'High Risk', color };
    } else if (type === 'compliance') {
      if (score >= 80) return { text: 'Compliant', color };
      if (score >= 60) return { text: 'Partially Compliant', color };
      return { text: 'Non-Compliant', color };
    } else if (type === 'maturity') {
      if (score >= 80) return { text: 'Optimized', color };
      if (score >= 60) return { text: 'Managed', color };
      return { text: 'Initial', color };
    }
  };

  const renderTrustDrilldown = (breakdown) => {
    const subcomponents = breakdown?.subcomponents || {};
    return (
      <>
        <Descriptions title="Trust Score Subcomponents" bordered column={1}>
          {Object.keys(subcomponents).map(key => (
            <Descriptions.Item
              key={key}
              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            >
              <Progress
                percent={subcomponents[key].score}
                size="small"
                strokeColor={getScoreColor(subcomponents[key].score)}
              />
            </Descriptions.Item>
          ))}
        </Descriptions>
        {breakdown?.penalties && breakdown.penalties.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Top Issues</Title>
            {breakdown.penalties.slice(0, 5).map((p, i) => (
              <Alert
                key={i}
                message={p.subcomponent?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                description={p.explanation}
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  const renderComplianceDrilldown = (breakdown) => {
    const frameworks = breakdown?.frameworks || {};
    return (
      <>
        <Descriptions title="Framework Scores" bordered column={1}>
          {Object.keys(frameworks).map(fw => (
            <Descriptions.Item key={fw} label={fw}>
              <Progress
                percent={frameworks[fw].score}
                size="small"
                strokeColor={getScoreColor(frameworks[fw].score)}
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {frameworks[fw].passed}/{frameworks[fw].total} controls passed
              </Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
        {breakdown?.penalties && breakdown.penalties.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Top Compliance Gaps</Title>
            {breakdown.penalties.slice(0, 5).map((p, i) => (
              <Alert
                key={i}
                message={`${p.framework} - ${p.control}`}
                description={p.explanation}
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  const renderMaturityDrilldown = (breakdown) => {
    const elements = breakdown?.elements || {};
    return (
      <>
        <Descriptions title="Maturity Elements" bordered column={1}>
          {Object.keys(elements).map(key => (
            <Descriptions.Item
              key={key}
              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            >
              <Progress
                percent={elements[key].score}
                size="small"
                strokeColor={getScoreColor(elements[key].score)}
              />
            </Descriptions.Item>
          ))}
        </Descriptions>
        {breakdown?.penalties && breakdown.penalties.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Top Maturity Gaps</Title>
            {breakdown.penalties.slice(0, 5).map((p, i) => (
              <Alert
                key={i}
                message={p.element?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                description={p.explanation}
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <Card>
        <Spin tip="Loading scores..." />
      </Card>
    );
  }

  if (error && !scores) {
    return (
      <Card>
        <Empty
          description={error}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<SyncOutlined />} onClick={handleComputeScores} loading={computing}>
            Compute Scores
          </Button>
        </Empty>
      </Card>
    );
  }

  if (!scores) {
    return null;
  }

  const trustBand = getScoreBand(scores.trust_score, 'trust');
  const complianceBand = getScoreBand(scores.compliance_score, 'compliance');
  const maturityBand = getScoreBand(scores.maturity_score, 'maturity');

  return (
    <>
      <Card
        title="TrustGrid Score Dashboard"
        extra={
          <Button icon={<SyncOutlined />} onClick={handleComputeScores} loading={computing}>
            Refresh Scores
          </Button>
        }
      >
        <Row gutter={16}>
          <Col span={8}>
            <ScoreGauge
              title="Trust Score"
              score={scores.trust_score}
              band={trustBand.text}
              color={trustBand.color}
              onClick={() => openDrilldown('trust', scores.trust_breakdown)}
            />
          </Col>
          <Col span={8}>
            <ScoreGauge
              title="Compliance Score"
              score={scores.compliance_score}
              band={complianceBand.text}
              color={complianceBand.color}
              onClick={() => openDrilldown('compliance', scores.compliance_breakdown)}
            />
          </Col>
          <Col span={8}>
            <ScoreGauge
              title="Governance Maturity"
              score={scores.maturity_score}
              band={maturityBand.text}
              color={maturityBand.color}
              onClick={() => openDrilldown('maturity', scores.maturity_breakdown)}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">
            Last updated: {new Date(scores.snapshot_at).toLocaleString()}
          </Text>
          {scores.decay_applied && (
            <Tag color="warning" style={{ marginLeft: 8 }}>Time Decay Applied</Tag>
          )}
        </div>
      </Card>

      <Modal
        title={`${selectedScore?.type?.replace(/\b\w/g, l => l.toUpperCase())} Score Details`}
        open={drilldownVisible}
        onCancel={() => setDrilldownVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDrilldownVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedScore?.type === 'trust' && renderTrustDrilldown(selectedScore.breakdown)}
        {selectedScore?.type === 'compliance' && renderComplianceDrilldown(selectedScore.breakdown)}
        {selectedScore?.type === 'maturity' && renderMaturityDrilldown(selectedScore.breakdown)}
      </Modal>
    </>
  );
};

export default ThreeScoreGauges;
